import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Fade,
  styled,
  alpha
} from '@mui/material';
import {
  Login as LoginIcon
} from '@mui/icons-material';
import { authService } from '../services/authService.ts';

// Styled components for modern design
const GradientBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 50%, #0E6655 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    animation: 'float 20s infinite ease-in-out',
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
    '50%': { transform: 'translate(50px, 50px) rotate(180deg)' },
  }
}));

const GlassCard = styled(Card)(({ theme }) => ({
  maxWidth: 450,
  width: '90%',
  backdropFilter: 'blur(20px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  borderRadius: 24,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
  padding: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 48px 0 rgba(31, 38, 135, 0.5)',
  }
}));

const OAuthButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #1ABC9C 0%, #16A085 100%)',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 16px rgba(26, 188, 156, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, #16A085 0%, #0E6655 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(26, 188, 156, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
  '&.Mui-disabled': {
    background: 'linear-gradient(135deg, #95A5A6 0%, #7F8C8D 100%)',
    color: 'white',
  }
}));

interface LoginProps {
  onLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleOAuthLogin = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('[Login] Initiating OAuth login...');

      // Redirect to Keycloak for authentication
      await authService.login();

      // Note: The page will redirect to Keycloak, so code after this won't execute
      // After successful auth, user will be redirected to /callback
    } catch (err) {
      console.error('[Login] OAuth login error:', err);
      setError('Failed to initiate login. Please try again.');
      setLoading(false);
    }
  };

  return (
    <GradientBackground>
      <Fade in={true} timeout={800}>
        <GlassCard elevation={8}>
          <CardContent sx={{ p: 4 }}>
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/precision_ai_logo.png"
                alt="PrecisionAI Logo"
                sx={{
                  width: '280px',
                  maxWidth: '100%',
                  height: 'auto',
                  mb: 3,
                  animation: 'fadeIn 1s ease-in-out',
                  '@keyframes fadeIn': {
                    '0%': { opacity: 0, transform: 'translateY(-20px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  }
                }}
              />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                  fontSize: '1.1rem'
                }}
              >
                Data Curation Pipeline
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Fade in={true}>
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* OAuth Login */}
            <Box sx={{ mt: 2 }}>
              {/* Main OAuth Login Button */}
              <OAuthButton
                fullWidth
                size="large"
                variant="contained"
                onClick={handleOAuthLogin}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <LoginIcon />}
              >
                {loading ? 'Redirecting...' : 'Sign in with Precision AI'}
              </OAuthButton>
            </Box>
          </CardContent>
        </GlassCard>
      </Fade>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 15s infinite ease-in-out reverse',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float 10s infinite ease-in-out',
        }}
      />
    </GradientBackground>
  );
};

export default Login;
